import { useEffect, useRef, useState } from 'react';

import { useOnlineStatus } from './useOnlineStatus';

// `??` so an intentionally-empty VITE_API_ENDPOINT (behind the worker proxy) stays same-origin.
const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT ?? 'http://localhost:4200';

/** Polled while all is well; faster while something is wrong, so recovery shows quickly. */
export const STATUS_POLL_MS = 60_000;
export const STATUS_POLL_TROUBLE_MS = 15_000;
const STATUS_TIMEOUT_MS = 8_000;
/** One failed check is a blip (a deploy restarting a pod); two in a row is "can't reach". */
const FAILURES_BEFORE_UNREACHABLE = 2;

export type ServiceState = 'ok' | 'degraded' | 'down';
/** `unreachable` is decided here, in the browser: the page loaded but the API did not answer. */
export type PlatformState = ServiceState | 'unreachable';

export interface PlatformIncident {
  title: string;
  message: string | null;
  severity: 'degraded' | 'down' | 'maintenance';
  component: string | null;
  since: string;
  until: string | null;
}

export interface PlatformStatusSnapshot {
  state: PlatformState;
  components: { id: string; name: string; state: ServiceState }[];
  /** What a platform admin posted, if anything is open; its title is what the icon says. */
  incident: PlatformIncident | null;
  release: { version: string; kind: string; date: string; headline: string | null } | null;
}

const INITIAL: PlatformStatusSnapshot = {
  state: 'ok',
  components: [],
  incident: null,
  release: null,
};

function isServiceState(value: unknown): value is ServiceState {
  return value === 'ok' || value === 'degraded' || value === 'down';
}

/**
 * Reads `GET /health/status` (public, no login) and keeps the answer current. Used by the
 * Revvly icon in the top nav and by the sign-in page. While the browser itself is offline it
 * reports nothing new: that is the user's network, and the offline banner already says so.
 */
export function usePlatformStatus(upToVersion?: string | null): PlatformStatusSnapshot {
  const online = useOnlineStatus();
  const [snapshot, setSnapshot] = useState<PlatformStatusSnapshot>(INITIAL);
  const failures = useRef(0);

  useEffect(() => {
    if (!online) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const check = async (): Promise<void> => {
      let next: PlatformStatusSnapshot | null = null;
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), STATUS_TIMEOUT_MS);
        try {
          const query = upToVersion ? `?upToVersion=${encodeURIComponent(upToVersion)}` : '';
          const response = await fetch(`${API_ENDPOINT}/health/status${query}`, {
            signal: controller.signal,
            credentials: 'omit',
          });
          if (!response.ok) throw new Error(`status ${response.status}`);
          const body = (await response.json()) as Partial<PlatformStatusSnapshot>;
          failures.current = 0;
          next = {
            state: isServiceState(body.state) ? body.state : 'ok',
            components: Array.isArray(body.components) ? body.components : [],
            incident: body.incident?.title ? body.incident : null,
            release: body.release ?? null,
          };
        } catch {
          failures.current += 1;
          if (failures.current >= FAILURES_BEFORE_UNREACHABLE) {
            next = { ...INITIAL, state: 'unreachable' };
          }
        } finally {
          clearTimeout(timeout);
        }
      }
      if (cancelled) return;
      if (next) setSnapshot(next);
      const trouble = failures.current > 0 || (next && next.state !== 'ok');
      timer = setTimeout(() => void check(), trouble ? STATUS_POLL_TROUBLE_MS : STATUS_POLL_MS);
    };

    void check();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [online, upToVersion]);

  return snapshot;
}
